/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import {
  BuilderFormSchemaFactory,
  BuilderUserContext, FormRootSchema, FormStateSchema,
  whenCreateOrEditMode,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  ContactTermsPayables,
  SystemVarsPayablesDto,
} from '@gh/builder-shared-data';
import {
  FormState,
  NumberTypes,
  toAppFormGroup,
  updateSchemaWithChildren,
} from '@gh/common-shared';
import { FormMode } from '@gh/core-ui';

import { isSystemVarsDayControlsByContactTermsEnabled } from '../../resources/model/system-vars.model';

const isBalanceRequired = (paymentDue: ContactTermsPayables) => {
  switch (paymentDue) {
    case ContactTermsPayables.InDays:
    case ContactTermsPayables.OnMonthDay:
    case ContactTermsPayables.DaysAfterEOM:
    case ContactTermsPayables.MonthDayAfterEOM:
      return true;
    default:
      return false;
  }
};

const isDayOfMonthType = (paymentDue: ContactTermsPayables) => {
  switch (paymentDue) {
    case ContactTermsPayables.OnMonthDay:
    case ContactTermsPayables.MonthDayAfterEOM:
      return true;
    default:
      return false;
  }
};

@Injectable()
export class SystemVarsPayablesTabFormState extends FormState<SystemVarsPayablesDto> {
  constructor(private userContext: BuilderUserContext,
              schemaFactory: BuilderFormSchemaFactory) {
    super(schemaFactory);
  }

  disableOrEnableControlsByPaymentDue(paymentDue: ContactTermsPayables): void {
    updateSchemaWithChildren(toAppFormGroup(this.group), {
      children: {
        payDefaultPaymentBalanceDays: {
          required: isBalanceRequired(paymentDue),
          type: isDayOfMonthType(paymentDue) ? NumberTypes.DayOfMonth : NumberTypes.Days,
          enabled: isSystemVarsDayControlsByContactTermsEnabled(paymentDue),
        },
        payDefaultPaymentDiscountDays: {
          enabled: isSystemVarsDayControlsByContactTermsEnabled(paymentDue),
        },
      },
    });
  }

  protected buildSchema(model: SystemVarsPayablesDto, { root, group, array }: FormStateSchema): FormRootSchema {
    const mode = this.userContext.hasPermission(BuilderPermission.Setup.SystemVariables.Payables.EDIT)
      ? FormMode.EditMode
      : FormMode.ViewMode;

    return root({
      enabled: whenCreateOrEditMode(),
      mode,
      optimisticLock: true,
      children: {
        contractLicnApplicable: void 0,
        payDefaultPaymentDue: { required: true },
        payDefaultPaymentBalanceDays: {
          required: isBalanceRequired(model.payDefaultPaymentDue),
          type: isDayOfMonthType(model.payDefaultPaymentDue) ? NumberTypes.DayOfMonth : NumberTypes.Days,
          enabled: isSystemVarsDayControlsByContactTermsEnabled(model.payDefaultPaymentDue),
        },
        payDefaultPaymentDiscountDays: {
          type: NumberTypes.Days,
          enabled: isSystemVarsDayControlsByContactTermsEnabled(model.payDefaultPaymentDue),
        },
        payDefaultPaymentDiscountPct: { type: NumberTypes.Percents_2 },
        trackingCaptions: array({
          children: model.trackingCaptions.map((_, index) => group({
            key: index.toString(),
            children: {
              value: void 0,
            },
          })),
        }),
      },
    });
  }
}
